/**
 * bandit.ts — a linear contextual bandit (LinUCB) with a sliding window and
 * reward-adaptive exploration.
 *
 * Algorithm:
 *   - Maintain ridge regression  A = λI + Σ_s φ_s φ_s^T,  b = Σ_s r_s φ_s.
 *   - Per round: solve U = A^{-1} b, score each candidate with
 *     U^T φ + β √(φ^T A^{-1} φ), pick the argmax.
 *   - Sliding window: keep the last N (φ, r) pairs. When the window slides
 *     we recompute A and b from scratch — O(N·M²) per round, cheap at the
 *     demo's scale (N=20, M=87 → ~80K ops).
 *
 * The exploration bonus β √(φ^T A^{-1} φ) is large where the agent has
 * little data (A^{-1} big) and small where it's confident — UCB's
 * "optimism in the face of uncertainty".
 *
 * ---
 *
 * **Reward-adaptive β.** A constant β is the wrong knob for a demo whose
 * whole point is "the agent moves to the centre and tracks drift". With a
 * fixed β the exploration bonus never decays, so even after the agent has
 * locked onto the flock + correct bait it keeps getting yanked off the peak
 * by candidates with high uncertainty — it jumps from the centre back to the
 * edge of the lake for no reason. That reads as "the algorithm is broken".
 *
 * Fix: scale β by how well the agent is doing right now. We track an EMA of
 * the observed reward and squeeze β toward a floor as reward rises. When the
 * fish drift and reward drops, β climbs back toward the configured maximum,
 * driving the agent to explore and re-find the peak. The demo's arc becomes
 * exactly what we want: explore hard, settle on the peak, track drift, only
 * re-explore when something changes.
 *
 *   r_ema ← (1 − α) · r_ema + α · r_t           (α = reward_ema)
 *   w     = clip((R_target − r_ema) / R_span, 0, 1)
 *   β_t   = β_floor + (β_max − β_floor) · w
 *
 * `R_target` is the catch rate a converged agent reaches (≈ 0.65). `R_span`
 * sets how quickly β reacts to a drop in reward — narrower means jumpier.
 * Setting `beta_floor = beta` disables the adaptation (constant β).
 */

import { dot, quadraticForm, argmax } from './linalg';

export interface LinUcbOptions {
  /** Ridge regularisation λ (keeps A invertible cold). */
  lambda: number;
  /** Exploration scale β at its largest — the value used when the agent is
   *  doing poorly and needs to explore. Bigger → more exploration. */
  beta: number;
  /** Sliding-window size N (§5.3). 0 means "no window" (use all data). */
  windowN: number;
  /** Lowest β is allowed to fall to once the agent is on the peak. The
   *  controller never goes below this, so the agent never goes fully greedy
   *  (it must still track drift). Default = β/4. */
  betaFloor: number;
  /** EMA smoothing factor for the reward signal ∈ (0, 1]. Smaller = more
   *  sluggish β changes; larger = jumpier. 0.15 ≈ a ~6-round half-life. */
  rewardEma: number;
  /** Reward level the controller treats as "on the peak". A converged agent
   *  catches ~65% of casts (the §4 catch landscape caps lower than 100%
   *  because casts near the flock still miss), so 0.65 is the natural
   *  target. β falls to β_floor when r_ema ≥ R_target. */
  rTarget: number;
  /** Reward range over which β ramps from floor to max. Narrower = the
   *  controller reacts harder to a given drop. 0.30 means a drop from 0.65
   *  to 0.35 takes β all the way back to max. */
  rSpan: number;
}

export const DEFAULT_OPTIONS: LinUcbOptions = {
  lambda: 0.15,
  beta: 0.1,
  windowN: 20,
  betaFloor: -1, // sentinel: resolve to beta/4 in the constructor
  rewardEma: 0.15,
  rTarget: 0.65,
  rSpan: 0.3,
};

interface Observation {
  phi: Float64Array;
  r: number;
}

export class LinUcb {
  readonly M: number;
  opts: LinUcbOptions;
  /** Current ridge estimate U = A^{-1} b. Public — the heat-map reads it. */
  U: Float64Array;
  /** Exploration β currently in effect (after the reward-adaptive scaling).
   *  Public — the panel shows it so the viewer can see exploration settle. */
  beta: number;
  /** Cached A^{-1}, refreshed once per round. */
  private Ainv: Float64Array | null = null;
  private data: Observation[] = [];
  /** Dirty flag — set when data changes so the main loop knows to refit. */
  private dirty = true;
  /** EMA of observed reward. Starts at 0 so β begins at its maximum — the
   *  agent explores hard cold, then β falls as reward rolls in. */
  private rewardEmaVal = 0;
  /** Resolved β floor (sentinel → beta/4 resolved in the constructor). */
  private betaFloorResolved: number;

  constructor(M: number, opts?: Partial<LinUcbOptions>) {
    this.M = M;
    this.opts = { ...DEFAULT_OPTIONS, ...opts };
    this.U = new Float64Array(M);
    // Resolve the floor sentinel: if the caller didn't set one, floor at β/4
    // — low enough to settle on the peak, high enough to keep tracking drift.
    this.betaFloorResolved =
      this.opts.betaFloor >= 0 ? this.opts.betaFloor : this.opts.beta / 4;
    // β starts at max — the agent has no data, reward EMA is 0.
    this.beta = this.opts.beta;
  }

  /** Number of observations currently in the window. */
  get nObs(): number {
    return this.data.length;
  }

  /**
   * Rebuild A and b from the current window and solve U = A^{-1} b.
   * Called once per round after an update; also recomputes A^{-1} for the
   * exploration bonus. O(N·M² + M³) — fine at demo scale.
   */
  private refit(): void {
    const { lambda } = this.opts;
    const M = this.M;
    const N = this.data.length;
    const A = new Float64Array(M * M);
    const b = new Float64Array(M);
    for (let i = 0; i < M; i++) A[i * M + i] = lambda;
    for (let s = 0; s < N; s++) {
      const phi = this.data[s].phi;
      const r = this.data[s].r;
      // A += phi phi^T (lower triangle, mirrored after).
      for (let i = 0; i < M; i++) {
        b[i] += r * phi[i];
        for (let j = 0; j <= i; j++) A[i * M + j] += phi[i] * phi[j];
      }
    }
    for (let i = 0; i < M; i++) {
      for (let j = 0; j < i; j++) A[j * M + i] = A[i * M + j];
    }
    this.U = choleskySolve(A, b, M);
    this.Ainv = invertSpd(A, M);
    this.dirty = false;
  }

  /** Ensure U and Ainv are current. Called by select() and predict(). */
  private ensureFit(): void {
    if (this.dirty) this.refit();
  }

  /**
   * Score a batch of candidate feature vectors with UCB and return the index
   * of the best. `features` are precomputed φ(a) for each candidate. Uses the
   * reward-adaptive `this.beta` (see the file header) — not the raw `opts.beta`.
   */
  select(features: Float64Array[]): number {
    const beta = this.beta;
    this.ensureFit();
    const Ai = this.Ainv;
    const M = this.M;
    const scores = new Array<number>(features.length);
    for (let i = 0; i < features.length; i++) {
      const phi = features[i];
      const mu = dot(this.U, phi);
      const bonus =
        Ai !== null ? beta * Math.sqrt(quadraticForm(phi, Ai, M)) : beta / Math.sqrt(this.opts.lambda);
      scores[i] = mu + bonus;
    }
    return argmax(scores);
  }

  /**
   * Record an observation and slide the window. `r` is the continuous reward
   * the agent learns from (typically p_catch ∈ [0, 1]). Also folds `r` into
   * the reward EMA and recomputes the adaptive β for the next round.
   */
  update(phi: Float64Array, r: number): void {
    this.data.push({ phi: phi.slice(), r });
    if (this.opts.windowN > 0 && this.data.length > this.opts.windowN) {
      this.data.splice(0, this.data.length - this.opts.windowN);
    }
    // Reward-adaptive β: track the reward EMA, then squeeze β toward its
    // floor as reward rises. See the file header for the full derivation.
    const { rewardEma: alpha, rTarget, rSpan, beta: betaMax } = this.opts;
    this.rewardEmaVal = (1 - alpha) * this.rewardEmaVal + alpha * r;
    const w = Math.max(0, Math.min(1, (rTarget - this.rewardEmaVal) / rSpan));
    this.beta = this.betaFloorResolved + (betaMax - this.betaFloorResolved) * w;
    this.dirty = true;
  }

  /** Predicted reward U^T φ — used by the heat-map. */
  predict(phi: Float64Array): number {
    this.ensureFit();
    return dot(this.U, phi);
  }

  /** Reset to a cold start (used when starting a new episode). */
  reset(): void {
    this.U = new Float64Array(this.M);
    this.Ainv = null;
    this.data = [];
    this.rewardEmaVal = 0;
    this.beta = this.opts.beta;
    this.dirty = true;
  }
}

/**
 * Solve A x = b via Cholesky factorisation. A must be symmetric positive
 * definite — the ridge term guarantees this. Returns a new Float64Array.
 */
function choleskySolve(A: Float64Array, b: Float64Array, d: number): Float64Array {
  const L = new Float64Array(d * d);
  for (let i = 0; i < d; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = A[i * d + j];
      for (let k = 0; k < j; k++) sum -= L[i * d + k] * L[j * d + k];
      if (i === j) {
        L[i * d + j] = Math.sqrt(Math.max(sum, 1e-12));
      } else {
        L[i * d + j] = sum / L[j * d + j];
      }
    }
  }
  const y = new Float64Array(d);
  for (let i = 0; i < d; i++) {
    let sum = b[i];
    for (let k = 0; k < i; k++) sum -= L[i * d + k] * y[k];
    y[i] = sum / L[i * d + i];
  }
  const x = new Float64Array(d);
  for (let i = d - 1; i >= 0; i--) {
    let sum = y[i];
    for (let k = i + 1; k < d; k++) sum -= L[k * d + i] * x[k];
    x[i] = sum / L[i * d + i];
  }
  return x;
}

/** Invert an SPD matrix via its Cholesky factor. Returns null if degenerate. */
function invertSpd(A: Float64Array, d: number): Float64Array | null {
  const L = new Float64Array(d * d);
  for (let i = 0; i < d; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = A[i * d + j];
      for (let k = 0; k < j; k++) sum -= L[i * d + k] * L[j * d + k];
      if (i === j) {
        if (sum <= 1e-12) return null;
        L[i * d + j] = Math.sqrt(sum);
      } else {
        L[i * d + j] = sum / L[j * d + j];
      }
    }
  }
  const Linv = new Float64Array(d * d);
  for (let col = 0; col < d; col++) {
    for (let i = col; i < d; i++) {
      let sum = i === col ? 1 : 0;
      for (let k = col; k < i; k++) sum -= L[i * d + k] * Linv[k * d + col];
      Linv[i * d + col] = sum / L[i * d + i];
    }
  }
  const Ainv = new Float64Array(d * d);
  for (let i = 0; i < d; i++) {
    for (let j = 0; j < d; j++) {
      let sum = 0;
      for (let k = Math.max(i, j); k < d; k++) {
        sum += Linv[k * d + i] * Linv[k * d + j];
      }
      Ainv[i * d + j] = sum;
    }
  }
  return Ainv;
}
